// ImportCollectionModal: Modal zum Importieren einer Sammlung per CSV oder Google Drive.
// Portiert die Import-Logik aus EmptyCollectionGuide und kapselt sie in einem SchemaModal.
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { FileUp, Cloud } from 'lucide-react';
import SchemaModal from '../ui/SchemaModal';
import Divider from '../ui/Divider';
import ButtonSpinner from '../ui/ButtonSpinner';
import { useToast } from '../ui/Toast';
import { queryClient } from '../queryClient';
import { apiClient, json } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './ImportCollectionModal.css';

// Extrahiert die Google Drive Datei-ID aus Link oder direkter ID
function extractDriveFileId(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  const matchPath = trimmed.match(/\/file\/d\/([^/]+)\//);
  if (matchPath && matchPath[1]) return matchPath[1];
  const matchQuery = trimmed.match(/[?&]id=([^&]+)/);
  if (matchQuery && matchQuery[1]) return matchQuery[1];
  return trimmed;
}

/**
 * ImportCollectionModal — Modal zum Importieren einer Sammlung.
 *
 * Props:
 *   open          — boolean
 *   mode          — 'csv' | 'drive'  (welcher Bereich visuell hervorgehoben wird)
 *   onOpenChange  — (open: boolean) => void
 *
 * Beispiel:
 *   <ImportCollectionModal open={open} mode="csv" onOpenChange={setOpen} />
 */
export default function ImportCollectionModal({ open, mode = 'csv', onOpenChange }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [driveLink, setDriveLink] = useState('');
  const [loadingDrive, setLoadingDrive] = useState(false);

  useEffect(() => {
    if (!open) setDriveLink('');
  }, [open]);

  const { push } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // CSV-Upload
  const handleUpload = useCallback(async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // FormData setzt seinen Content-Type mit Boundary selbst — deshalb json() statt apiClient.post
      await json('/api/upload-csv', { method: 'POST', body: formData });

      queryClient.invalidateQueries({ queryKey: ['cards'] });
      push({ tone: 'success', message: 'Sammlung importiert' });
      onOpenChange(false);
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('[ImportCollectionModal] Fehler beim CSV-Upload:', err);
      push({ tone: 'error', message: errorMessage(err, 'Import fehlgeschlagen.') });
    } finally {
      setUploading(false);
    }
  }, [uploading, logout, navigate, push, onOpenChange]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  // Google Drive Import
  const handleDriveLoad = useCallback(async () => {
    if (!driveLink.trim() || loadingDrive) return;
    setLoadingDrive(true);
    const fileId = extractDriveFileId(driveLink);
    try {
      await apiClient.post('/api/load-from-drive', { fileId });

      setDriveLink('');
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      push({ tone: 'success', message: 'Sammlung importiert' });
      onOpenChange(false);
    } catch (err) {
      // Fehlende Drive-Freigabe: der Server liefert die Google-Zustimmungs-Adresse mit;
      // die Datei-ID überdauert den Umweg über Google in der sessionStorage.
      if (err.code === 'DRIVE_AUTH_REQUIRED' && err.details?.authUrl) {
        sessionStorage.setItem('pendingDriveFileId', fileId);
        window.location.href = err.details.authUrl;
        return;
      }
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('[ImportCollectionModal] Fehler beim Laden von Google Drive:', err);
      push({ tone: 'error', message: errorMessage(err, 'Import fehlgeschlagen.') });
    } finally {
      setLoadingDrive(false);
    }
  }, [driveLink, loadingDrive, logout, navigate, push, onOpenChange]);

  return (
    <SchemaModal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title="Sammlung importieren"
      description="CSV-Datei hochladen oder per Google Drive importieren"
      dismissOnBackdrop={false}
    >
      <div className="import-collection-modal">
        {/* ── CSV-Upload ────────────────────── */}
        <section
          className={`import-collection-modal__section${mode === 'csv' ? ' import-collection-modal__section--primary' : ''}`}
        >
          <div className="import-collection-modal__section-header">
            <FileUp size={20} aria-hidden />
            <h3>CSV-Datei hochladen</h3>
          </div>
          <p className="import-collection-modal__hint">
            Exportiere deine Sammlung aus ManaBox und lade die CSV-Datei direkt hoch.
          </p>
          <div className="import-collection-modal__row">
            <button
              type="button"
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <><ButtonSpinner size={14} /> Wird importiert…</> : <><FileUp size={14} aria-hidden /> Datei auswählen</>}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFileChange}
              style={{ display: 'none' }}
            />
            <span className="import-collection-modal__file-hint">.csv (ManaBox-Export)</span>
          </div>
        </section>

        <Divider>oder per Google Drive</Divider>

        {/* ── Google Drive Import ───────────── */}
        <section
          className={`import-collection-modal__section${mode === 'drive' ? ' import-collection-modal__section--primary' : ''}`}
        >
          <div className="import-collection-modal__section-header">
            <Cloud size={20} aria-hidden />
            <h3>Google Drive Freigabe-Link</h3>
          </div>
          <p className="import-collection-modal__hint">
            Füge den Freigabe-Link deiner CSV-Datei auf Google Drive ein.
          </p>
          <div className="import-collection-modal__row">
            <input
              type="text"
              className="import-collection-modal__text-input"
              value={driveLink}
              onChange={(e) => setDriveLink(e.target.value)}
              placeholder="Google Drive Link oder Datei-ID…"
              onKeyDown={(e) => { if (e.key === 'Enter') handleDriveLoad(); }}
              disabled={loadingDrive}
              aria-label="Google Drive Freigabe-Link"
            />
            <button
              type="button"
              className="btn-primary"
              onClick={handleDriveLoad}
              disabled={!driveLink.trim() || loadingDrive}
            >
              {loadingDrive ? <><ButtonSpinner size={14} /> Lädt…</> : 'Importieren'}
            </button>
          </div>
          <p className="import-collection-modal__file-hint">
            Unterstützt: Freigabe-Links (<code>drive.google.com/file/d/…</code>) oder direkte Datei-IDs
          </p>
        </section>
      </div>
    </SchemaModal>
  );
}
